/**
 * ParkedChatsPanel — compact list of parked agent conversations for the widget toolbar.
 */
import React from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { Close as CloseIcon, DirectionsCar as ParkIcon } from '@mui/icons-material'
import type { ParkedThread } from '../utils/parkedStorage'

interface ParkedChatsPanelProps {
    chats: ParkedThread[]
    onResume: (chat: ParkedThread) => void
    onDismiss: (chatId: string) => void
}

function formatRelativeTime(timestamp: string): string {
    try {
        const diffMs = Date.now() - new Date(timestamp).getTime()
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMs / 3600000)
        if (diffMins < 1) return 'Just now'
        if (diffMins < 60) return `${diffMins}m ago`
        if (diffHours < 24) return `${diffHours}h ago`
        return new Date(timestamp).toLocaleDateString()
    } catch {
        return ''
    }
}

function preview(text: string, max = 36): string {
    const cleaned = text.replace(/\s+/g, ' ').trim()
    return cleaned.length <= max ? cleaned : `${cleaned.slice(0, max - 1)}…`
}

export const ParkedChatsPanel: React.FC<ParkedChatsPanelProps> = ({
    chats,
    onResume,
    onDismiss,
}) => {
    if (chats.length === 0) return null

    return (
        <Box
            sx={{
                position: 'absolute',
                bottom: '100%',
                right: 0,
                mb: 0.5,
                width: 280,
                maxHeight: 240,
                overflowY: 'auto',
                bgcolor: 'var(--card-bg, #fff)',
                border: '1px solid var(--card-border, #e2e8f0)',
                borderRadius: 2,
                boxShadow: 2,
                zIndex: 10,
            }}
        >
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid var(--card-border, #e2e8f0)' }}>
                <Typography variant="caption" fontWeight={600}>
                    Parked chats ({chats.length})
                </Typography>
            </Box>
            {chats.map(chat => {
                const isComplete = chat.status === 'complete'
                const isError = chat.status === 'error'
                return (
                    <Box
                        key={chat.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => onResume(chat)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onResume(chat)
                            }
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--card-border, #e2e8f0)',
                            bgcolor: isComplete
                                ? 'color-mix(in srgb, var(--success-color, #22c55e) 8%, transparent)'
                                : isError
                                  ? 'color-mix(in srgb, var(--error-color, #ef4444) 8%, transparent)'
                                  : 'transparent',
                            '&:hover': { bgcolor: 'var(--main-bg, #f0f4f8)' },
                        }}
                    >
                        <ParkIcon sx={{ fontSize: 16, mt: 0.25, color: 'var(--main-accent, #3b82f6)' }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" fontWeight={600} display="block">
                                {chat.agentName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                                {preview(chat.last_user_message)}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                                {formatRelativeTime(chat.parked_at)}
                                {isComplete && ' · Ready'}
                                {isError && ' · Error'}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            aria-label="Dismiss parked chat"
                            onClick={e => {
                                e.stopPropagation()
                                onDismiss(chat.id)
                            }}
                            sx={{ p: 0.25 }}
                        >
                            <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Box>
                )
            })}
        </Box>
    )
}

export default ParkedChatsPanel
